import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import fileDownload from "js-file-download";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import DownloadIcon from "@mui/icons-material/Download";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import PeopleIcon from "@mui/icons-material/People";
import LogoutIcon from "@mui/icons-material/Logout";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { AuthContext } from "../context/AuthContext";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import {
  Button,
  ListItemIcon,
  Tab,
  Tabs,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import { Dashboard } from "@mui/icons-material";
import LibraryBookingHistory from "./LibraryBookingHistory";
import AdminStudentList from "./AdminStudentList";
import toast from "react-hot-toast";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import AdminComplaints from "./AdminComplaints";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import ContactUs from "./ContactUs";
import StarIcon from "@mui/icons-material/Star";
import AdminReviews from "./AdminReviews";
import RoomLibraryManager from "../components/HostelLibraryManager";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import MonthlyFee from "./MonthlyFee";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import ArticleIcon from "@mui/icons-material/Article";
import AdminBlogs from "./AdminBlogs";
import AdminSwitchRequests from "./AdminSwitchRequests";
import SwitchRequestIcon from "@mui/icons-material/SwapHoriz";
import SavingsIcon from "@mui/icons-material/Savings";
import AdminDeposits from "./AdminDeposits";
import SettingsIcon from "@mui/icons-material/Settings";
import BillingSettings from "./BillingSettings";
import AdminRevenueDashboard from "./AdminRevenueDashboard";

const AdminDashboard = () => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [libraryStats, setLibraryStats] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Dashboard");
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const refreshAccessToken = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}token/refresh/`,
        {
          refresh: refreshToken,
        }
      );

      const { access, refresh } = response.data;
      localStorage.setItem("token", access);
      localStorage.setItem("refreshToken", refresh);

      return access;
    } catch (error) {
      console.error("Token refresh failed:", error);
      handleLogout();
      throw error;
    }
  };

  const makeAuthenticatedRequest = async (apiCall) => {
    try {
      return await apiCall();
    } catch (error) {
      if (error.response?.status === 401) {
        try {
          const newToken = await refreshAccessToken();
          const newConfig = {
            headers: { Authorization: `Bearer ${newToken}` },
          };

          // Retry the original request with new token
          return await apiCall(newConfig);
        } catch (refreshError) {
          handleLogout();
          throw refreshError;
        }
      }
      throw error;
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const downloadLibraryCSV = async () => {
    const loadingToast = toast.loading("Downloading library data...");
    try {
      const res = await makeAuthenticatedRequest(async (newConfig = config) => {
        return axios.get(
          `${process.env.REACT_APP_API_URL}library/admin/export-csv/`,
          {
            ...newConfig,
            responseType: "blob",
          }
        );
      });
      fileDownload(res.data, "library_bookings.csv");
      toast.dismiss(loadingToast);
      toast.success("Library CSV downloaded successfully!");
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to download Library CSV.");
    }
  };

  const fetchDashboardData = async () => {
    setIsLoading(true);
    const loadingToast = toast.loading("Loading dashboard data...");
    try {
      const [libraryStatsRes] = await Promise.all([
        makeAuthenticatedRequest(async (newConfig = config) => {
          return axios.get(
            `${process.env.REACT_APP_API_URL}library/admin/library-dashboard-stats/`,
            newConfig
          );
        }),
      ]);
      toast.dismiss(loadingToast);
      setLibraryStats(libraryStatsRes.data);
      setIsLoading(false);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to load dashboard data");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const drawerItems = [
    {
      label: "Dashboard",
      icon: (isSelected) => (
        <Dashboard className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        setSelectedTab("Dashboard");
        setMobileOpen(false);
      },
    },
    {
      label: "Revenue",
      icon: (isSelected) => (
        <TrendingUpIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        setSelectedTab("Revenue");
        setMobileOpen(false);
      },
    },
    {
      label: "Library History",
      icon: (isSelected) => (
        <LocalLibraryOutlinedIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        setSelectedTab("Library History");
        setMobileOpen(false);
      },
    },
    {
      label: "View All Students",
      icon: (isSelected) => (
        <PeopleIcon className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        setSelectedTab("View All Students");
        setMobileOpen(false);
      },
    },
    {
      label: "Monthly Fee",
      icon: (isSelected) => (
        <CurrencyRupeeIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        setSelectedTab("Monthly Fee");
        setMobileOpen(false);
      },
    },
    {
      label: "Billing Settings",
      icon: (isSelected) => (
        <SettingsIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        setSelectedTab("Billing Settings");
        setMobileOpen(false);
      },
    },

    {
      label: "Switch Requests",
      icon: (isSelected) => (
        <SwitchRequestIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        setSelectedTab("Switch Requests");
        setMobileOpen(false);
      },
    },
    {
      label: "Deposits",
      icon: (isSelected) => (
        <SavingsIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        setSelectedTab("Deposits");
        setMobileOpen(false);
      },
    },
    {
      label: "Blogs",
      icon: (isSelected) => (
        <ArticleIcon className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        setSelectedTab("Blogs");
        setMobileOpen(false);
      },
    },
    {
      label: "Complaints",
      icon: (isSelected) => (
        <ReportProblemIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        setSelectedTab("Complaints");
        setMobileOpen(false);
      },
    },
    {
      label: "Contacts",
      icon: (isSelected) => (
        <MarkEmailUnreadOutlinedIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        setSelectedTab("Contacts");
        setMobileOpen(false);
      },
    },
    {
      label: "Downloads",
      icon: (isSelected) => (
        <DownloadIcon className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        setSelectedTab("Downloads");
        setMobileOpen(false);
      },
    },
    {
      label: "Reviews",
      icon: (isSelected) => (
        <StarIcon className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        setSelectedTab("Reviews");
        setMobileOpen(false);
      },
    },
    {
      label: "Logout",
      icon: (isSelected) => (
        <LogoutIcon className={isSelected ? "text-white" : "text-red-600"} />
      ),
      action: () => {
        handleLogout();
        setMobileOpen(false);
      },
    },
  ];

  const drawerContent = (
    <Box className="h-full overflow-y-auto bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
      {/* Background decoration */}
      <Box className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500" />
      <Box className="absolute top-20 right-0 w-32 h-32 bg-blue-500/10 rounded-full -translate-y-16 translate-x-16" />
      <Box className="absolute bottom-20 left-0 w-24 h-24 bg-purple-500/10 rounded-full translate-y-12 -translate-x-12" />

      <Box className="relative z-10 h-full flex flex-col">
        <Box className="p-6 border-b border-gray-700/50">
          <Box className="flex items-center gap-4">
            <Avatar
              src="/images/logo3.png"
              alt="Prof. S. S. Patil Study Center"
              className="w-14 h-14 border-2 border-blue-500/30 shadow-lg"
            />
            <Box>
              <Typography variant="h6" className="font-bold text-white mb-1">
                Prof. S. S. Patil Study Center
              </Typography>
              <Typography
                variant="caption"
                className="text-gray-400 bg-gray-800/50 px-2 py-1 rounded-full"
              >
                Admin Panel
              </Typography>
            </Box>
          </Box>
        </Box>

        <List className="py-4 flex-1">
          {drawerItems.map((item) => (
            <ListItem
              button
              key={item.label}
              onClick={item.action}
              className={`mb-1 mx-3 rounded-xl cursor-pointer transition-all duration-300 ${
                selectedTab === item.label
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25"
                  : "text-gray-300 hover:bg-gray-700/50 hover:text-white"
              }`}
              sx={{
                "&:hover": {
                  transform:
                    selectedTab === item.label ? "none" : "translateX(6px)",
                },
              }}
            >
              <ListItemIcon className="min-w-0 mr-3">
                <Box
                  className={`p-1 rounded-lg ${
                    selectedTab === item.label
                      ? "bg-white/20 text-white"
                      : "bg-transparent"
                  }`}
                >
                  {item.icon(selectedTab === item.label)}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                className={selectedTab === item.label ? "font-semibold" : ""}
              />
              {selectedTab === item.label && (
                <Box className="w-2 h-2 bg-white rounded-full animate-pulse" />
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => {
    const getColorClasses = (color) => {
      const colorMap = {
        "bg-blue-600": {
          bg: "bg-gradient-to-r from-blue-500 to-blue-600",
          text: "text-blue-600",
          hoverText: "group-hover:text-blue-700",
          overlay: "bg-gradient-to-br from-blue-50 to-blue-100",
        },
        "bg-green-600": {
          bg: "bg-gradient-to-r from-green-500 to-green-600",
          text: "text-green-600",
          hoverText: "group-hover:text-green-700",
          overlay: "bg-gradient-to-br from-green-50 to-green-100",
        },
        "bg-green-500": {
          bg: "bg-gradient-to-r from-green-500 to-green-600",
          text: "text-green-600",
          hoverText: "group-hover:text-green-700",
          overlay: "bg-gradient-to-br from-green-50 to-green-100",
        },
        "bg-yellow-600": {
          bg: "bg-gradient-to-r from-yellow-500 to-yellow-600",
          text: "text-yellow-600",
          hoverText: "group-hover:text-yellow-700",
          overlay: "bg-gradient-to-br from-yellow-50 to-yellow-100",
        },
        "bg-blue-400": {
          bg: "bg-gradient-to-r from-blue-400 to-blue-500",
          text: "text-blue-600",
          hoverText: "group-hover:text-blue-700",
          overlay: "bg-gradient-to-br from-blue-50 to-blue-100",
        },
        "bg-purple-600": {
          bg: "bg-gradient-to-r from-purple-500 to-purple-600",
          text: "text-purple-600",
          hoverText: "group-hover:text-purple-700",
          overlay: "bg-gradient-to-br from-purple-50 to-purple-100",
        },
        "bg-teal-600": {
          bg: "bg-gradient-to-r from-teal-500 to-teal-600",
          text: "text-teal-600",
          hoverText: "group-hover:text-teal-700",
          overlay: "bg-gradient-to-br from-teal-50 to-teal-100",
        },
      };
      return colorMap[color] || colorMap["bg-blue-600"];
    };

    const colors = getColorClasses(color);

    return (
      <Card className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 cursor-pointer group stat-card border border-gray-100">
        <CardContent
          sx={{
            paddingBottom: "10px !important",
            paddingTop: "10px !important",
            paddingLeft: "10px !important",
            paddingRight: "10px !important",
          }}
          className="relative overflow-hidden"
        >
          {/* Background gradient overlay */}
          <div
            className={`absolute inset-0 ${colors.overlay} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
          ></div>

          <Box className="relative z-10 flex items-center gap-3">
            <Box
              className={`p-2 ${colors.bg} rounded-lg shadow-sm group-hover:shadow-md transition-shadow duration-300`}
            >
              {React.cloneElement(icon, { className: "text-white text-sm" })}
            </Box>
            <Box className="flex-1">
              <Typography
                variant="body2"
                className="text-gray-600 group-hover:text-gray-800 transition-colors duration-300 font-medium text-xs"
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" className="text-gray-500 text-xs">
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box className="text-right">
              <Typography
                variant="h6"
                className={`font-bold ${colors.text} ${colors.hoverText} transition-colors duration-300`}
              >
                {value}
              </Typography>
              {trend && (
                <Typography
                  variant="caption"
                  className={`text-${
                    trend > 0 ? "green" : "red"
                  }-600 text-xs font-medium`}
                >
                  {trend > 0 ? "+" : ""}
                  {trend}%
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderDashboard = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 md:p-4 lg:p-6">
      <div className="max-w-full mx-auto space-y-4 md:space-y-6">
        {/* Combined Statistics Row */}
        {libraryStats && (
          <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 dashboard-card">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Library Statistics */}
              {libraryStats && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-gradient-to-r from-green-500 to-green-600 rounded-lg">
                      <LocalLibraryIcon className="text-white text-lg" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">
                        Library
                      </h3>
                      <p className="text-gray-500 text-xs">Seat analytics</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <StatCard
                      title="Total"
                      value={libraryStats.library.total_seats}
                      icon={<LibraryBooksIcon />}
                      color="bg-green-600"
                    />
                    <StatCard
                      title="Booked"
                      value={libraryStats.library.booked}
                      icon={<CheckCircleOutlineIcon />}
                      color="bg-green-500"
                    />
                    <StatCard
                      title="Pending"
                      value={libraryStats.library.pending}
                      icon={<HourglassEmptyIcon />}
                      color="bg-yellow-600"
                    />
                    <StatCard
                      title="Available"
                      value={libraryStats.library.available}
                      icon={<EventAvailableIcon />}
                      color="bg-blue-400"
                    />
                    <StatCard
                      title="Today"
                      value={libraryStats.trends.today_bookings}
                      icon={<CalendarTodayIcon />}
                      color="bg-purple-600"
                    />
                    <StatCard
                      title="Weekly"
                      value={libraryStats.trends.weekly_bookings}
                      icon={<TrendingUpIcon />}
                      color="bg-teal-600"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Seat Maps Section */}
        <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 lg:p-6 dashboard-card">
          <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
            <div className="p-2 md:p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl">
              <BusinessRoundedIcon className="text-white text-lg md:text-2xl" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800">
                Live Availability Maps
              </h2>
              <p className="text-gray-600 text-xs md:text-sm">
                Real-time bed and seat availability
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-2 md:p-4 mb-2 md:mb-4">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              className="mb-2 md:mb-4"
              sx={{
                "& .MuiTab-root": {
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderRadius: "8px",
                  marginRight: "4px",
                  minHeight: "40px",
                  padding: "6px 12px",
                  "@media (min-width: 640px)": {
                    fontSize: "1rem",
                    borderRadius: "12px",
                    marginRight: "8px",
                    minHeight: "48px",
                    padding: "8px 16px",
                  },
                },
                "& .Mui-selected": {
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                },
              }}
            >
              <Tab
                label={
                  <div className="flex items-center gap-1 md:gap-2">
                    <LocalLibraryIcon className="text-sm md:text-base" />
                    <span className="hidden sm:inline">
                      Library Seat Availability
                    </span>
                    <span className="sm:hidden">Library</span>
                  </div>
                }
              />
            </Tabs>

            <div className="mt-2 md:mt-4">
              {activeTab === 0 && (
                <div className="bg-white rounded-lg p-2 md:p-4 shadow-sm overflow-hidden">
                  <RoomLibraryManager viewMode="room" />
                </div>
              )}
              {activeTab === 1 && (
                <div className="bg-white rounded-lg p-2 md:p-4 shadow-sm overflow-hidden">
                  <RoomLibraryManager viewMode="library" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDownloads = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-full mx-auto">
        {/* Download Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Library Downloads */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 rounded-lg">
                  <LocalLibraryIcon className="text-white text-2xl" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Library Bookings</h2>
                  <p className="text-green-100 text-sm">
                    Complete seat booking data export
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm font-bold">📚</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Data Format
                    </p>
                    <p className="text-xs text-gray-500">
                      CSV with detailed seat booking information
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm font-bold">🕒</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Time Slots
                    </p>
                    <p className="text-xs text-gray-500">
                      Booking times and durations
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm font-bold">📍</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Seat Information
                    </p>
                    <p className="text-xs text-gray-500">
                      Seat numbers and locations
                    </p>
                  </div>
                </div>
              </div>
              <Button
                variant="contained"
                onClick={downloadLibraryCSV}
                className="w-full mt-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg"
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: "12px", py: 1.5 }}
              >
                Download Library CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <span className="text-blue-600 text-2xl">💡</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Download Tips
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>
                  • CSV files can be opened in Excel, Google Sheets, or any
                  spreadsheet application
                </p>
                <p>
                  • Data includes booking dates, student information, and status
                  details
                </p>
                <p>
                  • Files are automatically named with the current date for easy
                  organization
                </p>
                <p>
                  • Large datasets may take a few moments to process and
                  download
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Box className="flex bg-gray-50 min-h-screen">
      {/* Mobile Menu Button */}
      <Box className="md:hidden fixed top-4 right-4 z-[30]">
        <IconButton
          onClick={() => setMobileOpen(true)}
          className="bg-white shadow-lg hover:bg-gray-50"
          size="large"
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
          zIndex: 55,
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
          zIndex: 10,
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box className="flex-1 md:ml-[280px] overflow-x-auto">
        {/* Header */}
        <Box className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-20">
          <Box className="flex items-center justify-between p-4 md:py-2">
            <Box className="flex items-center gap-3">
              <Box
                className={`p-3 rounded-xl shadow-lg ${
                  selectedTab === "Dashboard" &&
                  "bg-gradient-to-r from-blue-500 to-indigo-600"
                } ${
                  selectedTab === "Revenue" &&
                  "bg-gradient-to-r from-emerald-500 to-emerald-600"
                } ${
                  selectedTab === "Library History" &&
                  "bg-gradient-to-r from-green-600 to-green-700"
                } ${
                  selectedTab === "View All Students" &&
                  "bg-gradient-to-r from-purple-600 to-purple-700"
                } ${
                  selectedTab === "Monthly Fee" &&
                  "bg-gradient-to-r from-blue-600 to-indigo-600"
                } ${
                  selectedTab === "Billing Settings" &&
                  "bg-gradient-to-r from-blue-600 to-indigo-600"
                } ${
                  selectedTab === "Blogs" &&
                  "bg-gradient-to-r from-blue-600 to-indigo-600"
                } ${
                  selectedTab === "Complaints" &&
                  "bg-gradient-to-r from-red-600 to-red-700"
                } ${
                  selectedTab === "Contacts" &&
                  "bg-gradient-to-r from-blue-600 to-indigo-700"
                } ${
                  selectedTab === "Downloads" &&
                  "bg-gradient-to-r from-blue-500 to-indigo-600"
                } ${
                  selectedTab === "Reviews" &&
                  "bg-gradient-to-r from-yellow-600 to-orange-700"
                } ${
                  selectedTab === "Switch Requests" &&
                  "bg-gradient-to-r from-red-600 to-red-700"
                } ${
                  selectedTab === "Deposits" &&
                  "bg-gradient-to-r from-blue-600 to-indigo-600"
                }`}
              >
                {selectedTab === "Dashboard" && (
                  <Dashboard className="text-white text-2xl" />
                )}
                {selectedTab === "Revenue" && (
                  <TrendingUpIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Library History" && (
                  <LocalLibraryOutlinedIcon className="text-white text-2xl" />
                )}
                {selectedTab === "View All Students" && (
                  <PeopleIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Monthly Fee" && (
                  <CurrencyRupeeIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Billing Settings" && (
                  <SettingsIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Blogs" && (
                  <ArticleIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Complaints" && (
                  <ReportProblemIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Contacts" && (
                  <MarkEmailUnreadOutlinedIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Downloads" && (
                  <DownloadIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Reviews" && (
                  <StarIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Switch Requests" && (
                  <SwitchRequestIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Deposits" && (
                  <SavingsIcon className="text-white text-2xl" />
                )}
              </Box>
              <Box>
                <span className="mb-1 text-lg sm:text-3xl font-bold text-gray-800">
                  {selectedTab}
                </span>
                <Typography sx={{ fontSize: "16px" }} className="text-gray-600">
                  {selectedTab === "Dashboard" && "Overview & Analytics"}
                  {selectedTab === "Revenue" &&
                    "View revenue analytics, summaries, and trends"}
                  {selectedTab === "Library History" &&
                    "View and manage library booking records"}
                  {selectedTab === "View All Students" &&
                    "Student database and profile management"}
                  {selectedTab === "Monthly Fee" &&
                    "Configure Study Center fee structures"}
                  {selectedTab === "Billing Settings" &&
                    "Configure global billing behaviour and invoice cycle"}
                  {selectedTab === "Blogs" && "Manage blog posts"}
                  {selectedTab === "Complaints" && "Manage complaints"}
                  {selectedTab === "Contacts" && "Manage form submissions"}
                  {selectedTab === "Downloads" &&
                    "Export booking data and reports"}
                  {selectedTab === "Reviews" &&
                    "Monitor and respond to student reviews"}
                  {selectedTab === "Switch Requests" &&
                    "View and manage switch requests"}
                  {selectedTab === "Deposits" &&
                    "View and track student security deposits"}
                </Typography>
              </Box>
            </Box>
            <Box className="flex items-center gap-2 md:gap-3">
              <Box className="hidden md:flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span>Last updated:</span>
                <span className="font-medium">
                  {new Date().toLocaleTimeString()}
                </span>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Content Area */}
        <Box style={{ maxHeight: "calc(100vh - 120px)" }} className="flex-1">
          {isLoading ? (
            <Box className="flex items-center justify-center h-64">
              <Box className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <Typography variant="h6" className="text-gray-600">
                  Loading dashboard...
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              {selectedTab === "Dashboard" && renderDashboard()}
              {selectedTab === "Revenue" && <AdminRevenueDashboard />}
              {selectedTab === "Library History" && <LibraryBookingHistory />}
              {selectedTab === "View All Students" && <AdminStudentList />}
              {selectedTab === "Switch Requests" && <AdminSwitchRequests />}
              {selectedTab === "Monthly Fee" && <MonthlyFee />}
              {selectedTab === "Billing Settings" && <BillingSettings />}
              {selectedTab === "Blogs" && <AdminBlogs />}
              {selectedTab === "Complaints" && <AdminComplaints />}
              {selectedTab === "Contacts" && <ContactUs />}
              {selectedTab === "Downloads" && renderDownloads()}
              {selectedTab === "Reviews" && <AdminReviews />}
              {selectedTab === "Deposits" && <AdminDeposits />}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
